<template>
  <b-modal
    :id="`VMRSModal_${_uid}`"
    :ref="`VMRSModal`"
    title="VMRS"
    size="lg"
    :no-close-on-backdrop="true"
    @hide="cancel"
  >
    <div>
      Follow the prompts below to select the correct VMRS code. Available codes are based on Job Correction. If the
      correct code is not available, click Cancel to manually select the VMRS code
      <div class="mt-3">
        <h6 class="mb-0">System-Assembly</h6>
        The top System-Assembly code pairings are listed starting with the most likely pair.
      </div>
      <div v-if="isFetchingSystemAssembly" class="mt-2">
        <b-spinner small></b-spinner>
        Fetching System-Assembly List...
      </div>
      <v-select
        v-else
        v-model="selectedSystemAssembly"
        :options="systemAssemblyList"
        :filter-by="filterSystemAssembly"
        class="mt-2"
        placeholder="Select a System-Assembly Pairing"
      >
        <template #option="systemAssembly">
          <span class="text-uppercase">
            <b>{{ systemAssembly.systemCode }}</b>
            {{ systemAssembly.systemDescription }} -
            <b>{{ systemAssembly.assemblyCode }}</b>
            {{ systemAssembly.assemblyDescription }}
          </span>
        </template>
        <template #selected-option="systemAssembly">
          <span class="text-uppercase">
            <b>{{ systemAssembly.systemCode }}</b>
            {{ systemAssembly.systemDescription }} -
            <b>{{ systemAssembly.assemblyCode }}</b>
            {{ systemAssembly.assemblyDescription }}
          </span>
        </template>
      </v-select>
    </div>

    <div v-if="selectedSystemAssembly" class="mt-3">
      <h6 class="mb-0">Component</h6>
      Component codes for the selected System-Assembly pairing are listed starting with the most likely.
      <div v-if="isFetchingComponents" class="mt-2">
        <b-spinner small></b-spinner>
        Fetching Component List...
      </div>
      <v-select
        v-else
        v-model="selectedComponent"
        placeholder="Select a Component"
        :options="componentList"
        :filter-by="filterComponent"
      >
        <template #option="component">
          <span class="text-uppercase">
            <b>{{ component.componentCode }}</b>
            {{ component.componentDescription }}
          </span>
        </template>
        <template #selected-option="component">
          <span class="text-uppercase">
            <b>{{ component.componentCode }}</b>
            {{ component.componentDescription }}
          </span>
        </template>
      </v-select>
    </div>
    <template #modal-footer>
      <b-form-group>
        <div v-if="selectedSystemAssembly && selectedComponent">
          Approving this VMRS will overwrite any previously selected VMRS Code for this job
        </div>
        <label class="col-head">VMRS CODE:</label>
        <b-input class="vmrs-display" :value="displayCode" disabled>}</b-input>
      </b-form-group>
      <div class="mb-2">
        <b-button :disabled="!selectedSystemAssembly || !selectedComponent" size="sm" @click="approve()">
          Approve
        </b-button>
        <b-button class="ml-1" size="sm" @click="cancel">Cancel</b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { ServiceOrderActions, ServiceOrderGetters, ServiceOrderMutations } from '@/shared/store/service-order/types';
import { UserGetters } from '@/shared/store/user/types';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import moment from 'moment';
import vSelect from 'vue-select';
import ErrorService from '@/shared/services/ErrorService';

export default {
  name: 'VMRSModal',
  components: {
    'v-select': vSelect
  },
  data: function () {
    return {
      localJobKey: null,
      localCorrection: null,
      localSystemAssembly: null,
      selectedSystemAssembly: null,
      selectedComponent: null,
      isFetchingSystemAssembly: false,
      isFetchingComponents: false
    };
  },
  computed: {
    ...mapGetters({
      user: UserGetters.GET_USER_PROFILE,
      systemAssemblyList: ServiceOrderGetters.GET_AI_VMRS_SYSTEM_ASSEMBLY_LIST,
      componentList: ServiceOrderGetters.GET_AI_VMRS_COMPONENT_LIST
    }),
    hasLocalSystemAssembly() {
      return this.localSystemAssembly?.length > 0;
    },
    displayCode() {
      if (this.selectedSystemAssembly && !this.selectedComponent) {
        return this.selectedSystemAssembly.systemCode + ' - ' + this.selectedSystemAssembly.assemblyCode;
      } else if (this.selectedComponent) {
        return (
          this.selectedComponent.systemCode +
          ' - ' +
          this.selectedComponent.assemblyCode +
          ' - ' +
          this.selectedComponent.componentCode
        );
      } else {
        return '';
      }
    }
  },
  watch: {
    selectedSystemAssembly: {
      async handler() {
        if (this.selectedSystemAssembly) {
          try {
            this.isFetchingComponents = true;
            await this[ServiceOrderActions.FETCH_AI_VMRS_COMPONENT_LIST]({
              systemCode: this.selectedSystemAssembly.systemCode,
              assemblyCode: this.selectedSystemAssembly.assemblyCode,
              correction: this.localCorrection
            });
            this.selectedComponent = null;
          } catch {
            const errorMessage = `Error Generating VMRS Component Codes.`;
            ErrorService.createErrorToast(this, errorMessage);
          } finally {
            this.isFetchingComponents = false;
          }
        }
      },
      immediate: true
    }
  },
  methods: {
    ...mapMutations([
      ServiceOrderMutations.SET_AI_VMRS_SYSTEM_ASSEMBLY_LIST,
      ServiceOrderMutations.SET_AI_VMRS_COMPONENT_LIST,
      ServiceOrderMutations.SET_VMRS_CODE,
      ServiceOrderMutations.SET_VMRS_EMP_ID_APPROVED,
      ServiceOrderMutations.SET_VMRS_DATE_TIME_APPROVED
    ]),
    ...mapActions([
      ServiceOrderActions.FETCH_AI_VMRS_SYSTEM_ASSEMBLY_LIST,
      ServiceOrderActions.FETCH_AI_VMRS_COMPONENT_LIST
    ]),
    filterSystemAssembly(option, label, search) {
      return (
        (option.systemCode && option.systemCode.toLowerCase().indexOf(search.toLowerCase()) > -1) ||
        (option.systemDescription && option.systemDescription.toLowerCase().indexOf(search.toLowerCase()) > -1) ||
        (option.assemblyCode && option.assemblyCode.toLowerCase().indexOf(search.toLowerCase()) > -1) ||
        (option.assemblyDescription && option.assemblyDescription.toLowerCase().indexOf(search.toLowerCase()) > -1)
      );
    },
    filterComponent(option, label, search) {
      return (
        (option.componentCode && option.componentCode.toLowerCase().indexOf(search.toLowerCase()) > -1) ||
        (option.componentDescription && option.componentDescription.toLowerCase().indexOf(search.toLowerCase()) > -1)
      );
    },
    approve() {
      this.selectedComponent;
      this[ServiceOrderMutations.SET_VMRS_CODE]({
        jobKey: this.localJobKey,
        vmrsCodeByJob: {
          ...this.selectedComponent
        }
      });
      this[ServiceOrderMutations.SET_VMRS_EMP_ID_APPROVED]({ jobKey: this.localJobKey, empId: this.user.employeeId });
      this[ServiceOrderMutations.SET_VMRS_DATE_TIME_APPROVED]({
        jobKey: this.localJobKey,
        dateTimeApproved: moment()
      });
      this.cancel();
    },
    async show(correction, jobKey) {
      try {
        this.$bvModal.show(`VMRSModal_${this._uid}`);
        this.isFetchingSystemAssembly = true;
        this.localJobKey = jobKey;
        this.localCorrection = correction;

        await this[ServiceOrderActions.FETCH_AI_VMRS_SYSTEM_ASSEMBLY_LIST]({ correction: correction });
        this.isFetchingSystemAssembly = false;
      } catch {
        const errorMessage = `Error Generating VMRS System and Assembly Codes.`;
        ErrorService.createErrorToast(this, errorMessage);
        this.cancel();
        throw Error(errorMessage);
      } finally {
        this.isFetchingSystemAssembly = false;
      }
    },
    cancel() {
      this[ServiceOrderMutations.SET_AI_VMRS_SYSTEM_ASSEMBLY_LIST]([]);
      this[ServiceOrderMutations.SET_AI_VMRS_COMPONENT_LIST]([]);
      this.localCorrection = null;
      this.localJobKey = null;
      this.selectedSystemAssembly = null;
      this.selectedComponent = null;
      this.$bvModal.hide(`VMRSModal_${this._uid}`);
    }
  }
};
</script>
<style>
.modal-footer {
  justify-content: space-between;
  display: flex;
  align-items: flex-end;
}
.vmrs-display {
  max-width: fit-content;
}
</style>
